import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface TestRunItem {
  runId: string;
  projectId: string;
  specId: string;
  runNumber: number;
  title: string;
  status: 'READY' | 'IN_PROGRESS' | 'COMPLETED' | 'ABORTED';
  totalCount: number;
  passCount: number;
  failCount: number;
  blockedCount: number;
  skipCount: number;
  notRunCount: number;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: { id: string; name: string; email: string; avatarUrl: string | null };
}

export interface TestRunDetail extends TestRunItem {
  dashboard: {
    byType: Record<string, { total: number; pass: number; fail: number }>;
    byPriority: Record<string, { total: number; pass: number; fail: number }>;
    previousComparison: { previousRunNumber: number; passDiff: number; failDiff: number } | null;
  };
}

export interface TestRunResultItem {
  resultId: string;
  runId: string;
  testCaseId: string;
  status: 'NOT_RUN' | 'PASS' | 'FAIL' | 'BLOCKED' | 'SKIP';
  actualResult: string | null;
  attachments: unknown;
  testEnvSnapshot: unknown;
  executedBy: string | null;
  executedAt: string | null;
  linkedIssueIds: string[];
  createdAt: string;
  updatedAt: string;
  testCase: {
    id: string;
    tcId: string;
    title: string;
    type: string;
    priority: string;
    precondition: string | null;
    steps: { step: number; action: string }[];
    expectedResult: string | null;
    linkedIssues: { id: string; issueId: string; title: string; status: string }[];
  };
  previousStatus: string | null;
}

export interface FailActionResult {
  action: 'created' | 'reopened';
  issue: { id: string; issueId: string; title: string; status: string } | null;
}

// --- API calls ---

/** GET /specs/:specId/test-runs/active → 진행 중인 TestRun 조회 */
export async function fetchActiveRun(projectId: string, specId: string) {
  const res = await api.get<ApiResponse<TestRunItem | null>>(
    `/projects/${projectId}/specs/${specId}/test-runs/active`,
  );
  return res.data.data ?? null;
}

/** POST /specs/:specId/test-runs → 새 TestRun 생성 */
export async function createTestRun(projectId: string, specId: string) {
  const res = await api.post<ApiResponse<TestRunItem>>(
    `/projects/${projectId}/specs/${specId}/test-runs`,
  );
  return res.data.data!;
}

/** GET /specs/:specId/test-runs → TestRun 목록 */
export async function fetchRunList(projectId: string, specId: string) {
  const res = await api.get<ApiResponse<TestRunItem[]>>(
    `/projects/${projectId}/specs/${specId}/test-runs`,
  );
  return res.data.data!;
}

/** GET /specs/:specId/test-runs/:runId → TestRun 상세 + 대시보드 */
export async function fetchRunDetail(projectId: string, specId: string, runId: string) {
  const res = await api.get<ApiResponse<TestRunDetail>>(
    `/projects/${projectId}/specs/${specId}/test-runs/${runId}`,
  );
  return res.data.data!;
}

/** GET /test-runs/:runId/results → 결과 목록 (이전 결과 포함) */
export async function fetchResults(projectId: string, runId: string) {
  const res = await api.get<ApiResponse<TestRunResultItem[]>>(
    `/projects/${projectId}/test-runs/${runId}/results`,
  );
  return res.data.data!;
}

/** PUT /test-runs/:runId/results/:resultId → 결과 업데이트 */
export async function updateResult(
  projectId: string,
  runId: string,
  resultId: string,
  data: { status: string; actualResult?: string },
) {
  const res = await api.put<ApiResponse<TestRunResultItem>>(
    `/projects/${projectId}/test-runs/${runId}/results/${resultId}`,
    data,
  );
  return res.data.data!;
}

/** PUT /specs/:specId/test-runs/:runId/status → TestRun 상태 변경 */
export async function updateRunStatus(
  projectId: string,
  specId: string,
  runId: string,
  status: 'COMPLETED' | 'ABORTED',
) {
  const res = await api.put<ApiResponse<TestRunItem>>(
    `/projects/${projectId}/specs/${specId}/test-runs/${runId}/status`,
    { status },
  );
  return res.data.data!;
}

/** POST /test-runs/:runId/results/:resultId/fail-action → FAIL 이슈 처리 */
export async function handleFailAction(
  projectId: string,
  runId: string,
  resultId: string,
  data: {
    action: 'CREATE_NEW' | 'REOPEN';
    issueId?: string;
    priority?: string;
    assigneeId?: string;
  },
) {
  const res = await api.post<ApiResponse<FailActionResult>>(
    `/projects/${projectId}/test-runs/${runId}/results/${resultId}/fail-action`,
    data,
  );
  return res.data.data!;
}
